/* eslint-disable prettier/prettier */
/* eslint-disable import/order */
import { FC } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Button, Stack, TextField } from '@mui/material';
import { Form } from '@bvi/common-components';
import { useUpdateOrganizationTypeMutation, useGetByIdOrganizationTypeQuery } from '@bvi/admin-panel/entities/organization-type/api-slice';

interface IEditOrganizationTypeFormProperties {
  id: number;
  onSuccess: () => void;
}

interface IEditOrganizationTypeFormValues {
  name: string;
}

export const EditOrganizationTypeForm: FC<IEditOrganizationTypeFormProperties> = ({
  id,
  onSuccess,
}) => {
  const { t } = useTranslation('translation');
  const [updateOrganizationType, { isLoading: isUpdatingOrganizationType }] = useUpdateOrganizationTypeMutation();
  const { data: organizationTypeData } = useGetByIdOrganizationTypeQuery(id);

  const organizationType = organizationTypeData?.success ? organizationTypeData.payload.data : null;

  const validationSchema = z.object({
    name: z.string().min(1, t('manageList.form.fields.name.label') as string),
  });

  const methods = useForm<IEditOrganizationTypeFormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: organizationType?.name ?? '',
    },
    values: organizationType ? {
      name: organizationType.name,
    } : undefined,
  });

  const onSubmit = async (data: IEditOrganizationTypeFormValues) => {
    try {
      await updateOrganizationType([id, { name: data.name }]).unwrap();
      onSuccess();
    } catch {
      // Error handling - mutation will show error state
    }
  };

  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <TextField
            {...methods.register('name')}
            label={t('manageList.form.fields.name.label')}
            placeholder={t('manageList.form.fields.name.placeholder')}
            error={Boolean(methods.formState.errors.name)}
            helperText={methods.formState.errors.name?.message}
            fullWidth
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onSuccess}
              disabled={isUpdatingOrganizationType}
            >
              {t('manageList.form.actions.cancel.caption')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isUpdatingOrganizationType}
            >
              {t('manageList.form.actions.save.caption')}
            </Button>
          </Stack>
        </Stack>
      </Form>
    </FormProvider>
  );
};

